import { usePostSettlementConfirm } from '@/services/settlement/settlementManagement/useSettlementManagementQueries'
import { SettlementConfirmReq } from '@/services/settlement/settlementManagement/types'
import MConfirm from '@/utils/modal/MConfirm'
import { omit } from 'lodash-es'
import MToast from '@/utils/modal/MToast'
import MAlert from '@/utils/modal/MAlert'
import { VStack } from '@musinsa/mwms-ds'
import { colorSystem } from '@/styles/theme'
import Text from '@/components/content/Text'

export const useSettlementConfirm = () => {
  const settlementConfirmMutation = usePostSettlementConfirm()

  const confirmSettlement = async (params: SettlementConfirmReq) => {
    if (settlementConfirmMutation.isPending) return
    try {
      const response = await settlementConfirmMutation.mutateAsync(params)

      const hasUnsettledPartners = response.data.notFound.length > 0
      const isAllPartnersUnsettled = response.data.notFound.length === params.partnerIds.length

      if (isAllPartnersUnsettled) {
        MAlert.show(
          <VStack>
            <Text kind="Headline_20_bold" color={colorSystem.text.common.primary}>
              월 정산이 완료되지 않아 마감확정 할 수 없어요.
            </Text>
            <Text kind="Headline_20_bold" color={colorSystem.text.common.primary}>
              잠시후 다시 시도해 주세요.
            </Text>
          </VStack>,
          {
            subContent: (
              <Text kind="Body_14_regular" color={colorSystem.text.common.secondary}>
                월 정산은 매월 1일 오후 1시에 완료됩니다.
              </Text>
            ),
          },
        )

        return
      }

      if (hasUnsettledPartners) {
        MAlert.show(
          <VStack>
            <Text kind="Headline_20_bold" color={colorSystem.text.common.primary}>
              월 정산이 완료되지 않은 화주사를 제외하고
            </Text>
            <Text kind="Headline_20_bold" color={colorSystem.text.common.primary}>
              마감 확정되었습니다.
            </Text>
          </VStack>,
          {
            subContent: (
              <VStack>
                <Text kind="Body_14_regular" color={colorSystem.text.common.secondary}>
                  최대 30분 정도 소요되므로 아래 화주사는 잠시후 다시 시도해 주세요.
                </Text>
                <Text kind="Body_14_regular" color={colorSystem.text.common.secondary}>
                  {response.data.notFound.join(', ')}
                </Text>
              </VStack>
            ),
          },
        )
      }
      MToast.show('마감 확정되었습니다.')
    } catch (e) {
      MAlert.show(`일시적인 오류로 처리에 실패했어요.\n잠시 후 다시 시도해주세요.`, {
        subContent: '반복적으로 실패할 경우, 관리자에게 문의해 주세요.',
      })
    }
  }

  const handleSettlementConfirm = (
    params: Omit<SettlementConfirmReq, 'partnerIds'> & {
      partnerIds?: string[]
      partnerId?: string
    },
  ) =>
    MConfirm.show(
      `${params.partnerIds ? `선택한 ${params.partnerIds.length}개 화주사의 ` : ''}정산을 마감 확정하시겠어요?\n확정 후에는 취소가 불가합니다.`,
      {
        subContent: '무신사 정산 시스템 반영까지 최대 30분 정도 소요됩니다.',
        confirmText: '마감 확정',
        closeText: '취소',
        onConfirm: () =>
          confirmSettlement({
            ...omit(params, ['partnerId']),
            partnerIds: [...(params.partnerIds ?? []), ...(params.partnerId ? [params.partnerId] : [])],
          }),
      },
    )

  return { handleSettlementConfirm }
}
